import Editor from "@monaco-editor/react"
import AddIcon from "@mui/icons-material/Add"
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import LoadingButton from "@mui/lab/LoadingButton"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import { fmtTry, fmtUsd } from "@utils/currency"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import { useMemo, useState } from "react"
import { runPromptOnce } from "./api"
import {
  buildRequest,
  extractConditionNames,
  extractVariableNames,
  type PromptDraft
} from "./runner"
import type {
  PlaygroundMessage,
  PlaygroundResponse,
  PlaygroundRole,
  PlaygroundSettings
} from "./types"
import { useTryRate } from "./useTryRate"

interface RunTabProps {
  draft: PromptDraft
  settings: PlaygroundSettings
  onSettingsChange: (patch: Partial<PlaygroundSettings>) => void
  onSaveAsCase: (params: {
    query: string
    variables: Record<string, string>
    conditions: Record<string, boolean>
    previousMessages: PlaygroundMessage[]
    output: string
  }) => void
}

function MetricChip({ label, value }: { label: string; value: string }) {
  return (
    <Stack
      sx={{
        px: 1,
        py: 0.25,
        borderRadius: 1,
        bgcolor: "action.hover",
        minWidth: 78
      }}
    >
      <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700} lineHeight={1.3}>
        {value}
      </Typography>
    </Stack>
  )
}

export default function RunTab(props: RunTabProps) {
  const { draft, settings, onSettingsChange, onSaveAsCase } = props
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<PlaygroundResponse | null>(null)
  const tryRate = useTryRate()

  const variableNames = useMemo(
    () => extractVariableNames(draft.systemPrompt),
    [draft.systemPrompt]
  )

  const conditionNames = useMemo(
    () => extractConditionNames(draft.systemPrompt),
    [draft.systemPrompt]
  )

  const rawJson = useMemo(() => {
    if (result?.raw === undefined) return ""
    try {
      return JSON.stringify(result.raw, null, 2)
    } catch {
      return String(result.raw)
    }
  }, [result])

  const isJsonOutput = useMemo(() => {
    if (!result?.output) return false
    const trimmed = result.output.trim()
    return trimmed.startsWith("{") || trimmed.startsWith("[")
  }, [result])

  async function handleRun() {
    setRunning(true)
    const response = await runPromptOnce(
      buildRequest(
        {
          draft,
          repeats: 1,
          concurrency: 1,
          baseVariables: settings.variables,
          baseConditions: settings.conditions,
          temperature:
            settings.temperature === ""
              ? undefined
              : Number(settings.temperature),
          maxTokens:
            settings.maxTokens === "" ? undefined : Number(settings.maxTokens),
          timeoutMs:
            settings.timeoutMs === "" ? undefined : Number(settings.timeoutMs)
        },
        {
          query: settings.query,
          previousMessages: settings.previousMessages
        }
      )
    )
    setResult(response)
    setRunning(false)
  }

  function updateMessage(index: number, patch: Partial<PlaygroundMessage>) {
    const next = settings.previousMessages.map((message, i) =>
      i === index ? { ...message, ...patch } : message
    )
    onSettingsChange({ previousMessages: next })
  }

  return (
    <Stack direction="row" spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
      <Stack
        spacing={1.5}
        sx={{ width: "45%", minWidth: 340, overflow: "auto", pr: 0.5 }}
      >
        {conditionNames.length > 0 && (
          <Paper variant="outlined" sx={{ p: 1.25 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
            >
              KOŞULLU BLOKLAR ({conditionNames.length})
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap mt={0.5}>
              {conditionNames.map((name) => (
                <FormControlLabel
                  key={name}
                  sx={{ mr: 2 }}
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(settings.conditions[name])}
                      onChange={(event) =>
                        onSettingsChange({
                          conditions: {
                            ...settings.conditions,
                            [name]: event.target.checked
                          }
                        })
                      }
                    />
                  }
                  label={
                    <Typography variant="caption">{`{{#if_${name}}}`}</Typography>
                  }
                />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Kapalı blokların içeriği prompttan tamamen çıkarılır (production
              ile aynı davranış).
            </Typography>
          </Paper>
        )}

        {variableNames.length > 0 && (
          <Paper variant="outlined" sx={{ p: 1.25 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
            >
              DEĞİŞKENLER ({variableNames.length})
            </Typography>
            <Stack spacing={1} mt={1}>
              {variableNames.map((name) => (
                <TextField
                  key={name}
                  size="small"
                  label={`{{${name}}}`}
                  multiline
                  maxRows={6}
                  value={settings.variables[name] ?? ""}
                  onChange={(event) =>
                    onSettingsChange({
                      variables: {
                        ...settings.variables,
                        [name]: event.target.value
                      }
                    })
                  }
                />
              ))}
            </Stack>
          </Paper>
        )}

        <Accordion
          variant="outlined"
          disableGutters
          sx={{ "&:before": { display: "none" } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
            >
              KONUŞMA GEÇMİŞİ ({settings.previousMessages.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {settings.previousMessages.map((message, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Stack
                  key={index}
                  direction="row"
                  spacing={0.5}
                  alignItems="flex-start"
                >
                  <Select
                    size="small"
                    value={message.role}
                    sx={{ minWidth: 108 }}
                    onChange={(event) =>
                      updateMessage(index, {
                        role: event.target.value as PlaygroundRole
                      })
                    }
                  >
                    <MenuItem value="user">user</MenuItem>
                    <MenuItem value="assistant">assistant</MenuItem>
                    <MenuItem value="system">system</MenuItem>
                  </Select>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    maxRows={4}
                    value={message.content}
                    onChange={(event) =>
                      updateMessage(index, { content: event.target.value })
                    }
                  />
                  <IconButton
                    size="small"
                    onClick={() =>
                      onSettingsChange({
                        previousMessages: settings.previousMessages.filter(
                          (_, i) => i !== index
                        )
                      })
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Box>
                <Chip
                  icon={<AddIcon />}
                  size="small"
                  label="Mesaj ekle"
                  onClick={() =>
                    onSettingsChange({
                      previousMessages: [
                        ...settings.previousMessages,
                        { role: "user", content: "" }
                      ]
                    })
                  }
                />
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <TextField
          label="Kullanıcı mesajı (query)"
          size="small"
          multiline
          minRows={5}
          value={settings.query}
          onChange={(event) => onSettingsChange({ query: event.target.value })}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
              event.preventDefault()
              handleRun()
            }
          }}
        />

        <Stack direction="row" spacing={1} alignItems="center">
          <LoadingButton
            variant="contained"
            size="small"
            loading={running}
            startIcon={<PlayArrowIcon />}
            disabled={!settings.query.trim() || !draft.model}
            onClick={handleRun}
          >
            Çalıştır
          </LoadingButton>
          <Typography variant="caption" color="text.secondary">
            ⌘/Ctrl + Enter
          </Typography>
        </Stack>
      </Stack>

      <Divider orientation="vertical" flexItem />

      <Stack spacing={1} sx={{ flex: 1, minWidth: 0, overflow: "auto" }}>
        {!result && (
          <Alert severity="info" variant="outlined">
            Editördeki prompt metni, seçili model ve response schema ile canlı
            olarak çalıştırılır. DB’deki kayıtlı sürüm kullanılmaz.
          </Alert>
        )}

        {result && (
          <>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                color={result.ok ? "success" : "error"}
                label={result.ok ? "OK" : "HATA"}
              />
              <MetricChip label="Süre" value={`${result.latencyMs} ms`} />
              <MetricChip
                label="Token"
                value={`${result.usage.inputToken}→${result.usage.outputToken}`}
              />
              <MetricChip
                label="Maliyet"
                value={`${fmtUsd(result.usage.amount, 6)}${fmtTry(
                  result.usage.amount,
                  tryRate
                )}`}
              />
              <MetricChip label="Model" value={result.model || "-"} />
              {result.maxTokens ? (
                <MetricChip
                  label="maxTok"
                  value={result.maxTokens.toLocaleString()}
                />
              ) : null}
              <Tooltip title="Bu sonucu hazır soru olarak kaydet">
                <IconButton
                  size="small"
                  onClick={() =>
                    onSaveAsCase({
                      query: settings.query,
                      variables: settings.variables,
                      conditions: settings.conditions,
                      previousMessages: settings.previousMessages,
                      output: result.output
                    })
                  }
                >
                  <BookmarkAddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            {result.error && <Alert severity="error">{result.error}</Alert>}

            {result.unresolvedVariables.length > 0 && (
              <Alert severity="warning" variant="outlined">
                Değeri verilmemiş placeholder:{" "}
                {result.unresolvedVariables.map((v) => `{{${v}}}`).join(", ")}
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 0, overflow: "hidden" }}>
              {isJsonOutput ? (
                <Editor
                  height="340px"
                  defaultLanguage="json"
                  value={result.output}
                  options={{
                    readOnly: true,
                    minimap: { enabled: false },
                    fontSize: 12,
                    wordWrap: "on",
                    scrollBeyondLastLine: false
                  }}
                />
              ) : (
                <Box
                  sx={{
                    p: 1.25,
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                    fontSize: 13,
                    maxHeight: 340,
                    overflow: "auto"
                  }}
                >
                  {result.output || "—"}
                </Box>
              )}
            </Paper>

            <Accordion
              variant="outlined"
              disableGutters
              sx={{ "&:before": { display: "none" } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                >
                  MODELE GİDEN SYSTEM PROMPT
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                    fontSize: 12,
                    maxHeight: 300,
                    overflow: "auto"
                  }}
                >
                  {result.resolvedSystemPrompt || "—"}
                </Box>
              </AccordionDetails>
            </Accordion>

            {result.raw !== undefined && (
              <Accordion
                variant="outlined"
                disableGutters
                sx={{ "&:before": { display: "none" } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flex={1}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                    >
                      HAM PROVIDER YANITI (OpenAI / Gemini)
                    </Typography>
                    <Box flex={1} />
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon fontSize="small" />}
                      onClick={(event) => {
                        event.stopPropagation()
                        navigator.clipboard
                          .writeText(rawJson)
                          .then(() => showSuccessToast("Ham yanıt kopyalandı"))
                          .catch(() => showErrorToast("Kopyalanamadı"))
                      }}
                    >
                      Kopyala
                    </Button>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Editor
                    height="360px"
                    defaultLanguage="json"
                    value={rawJson}
                    options={{
                      readOnly: true,
                      minimap: { enabled: false },
                      fontSize: 12,
                      wordWrap: "on",
                      scrollBeyondLastLine: false
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}
      </Stack>
    </Stack>
  )
}
